import { Callout } from './parts';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LockClockOutlined from '@mui/icons-material/LockClockOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import type { Schemas } from '../../api/client';
import { ApiError } from '../../api/client';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState, LoadingRows } from '../../components/states';
import { StatusChip, humanize } from '../../components/StatusChip';
import { Field, Panel, StatGrid, StatTile } from '../../components/ui';
import { count, dateTime, relativeTime } from '../../lib/format';
import { fetchSupportSummary, tenantKeys, type TenantDetail } from './api';
import { FieldGrid } from './parts';

type Summary = Schemas['SupportSummary'];
type Staff = Summary['staff'][number];
type Device = Summary['devices'][number];
type SyncError = Schemas['SupportSyncError'];
type AuditEntry = Schemas['AuditEntry'];

const time = (v: string | null) => (
  <span className="tabular" style={{ whiteSpace: 'nowrap' }}>
    {v ? dateTime(v) : '—'}
  </span>
);

const staffColumns: Column<Staff>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (s) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {s.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {s.email ?? 'No email'}
        </Typography>
      </Box>
    ),
  },
  {
    key: 'roles',
    header: 'Roles',
    hideBelow: 'sm',
    render: (s) => (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        {s.roles.map((r) => (
          <Chip key={r} size="small" variant="outlined" label={r} />
        ))}
      </Stack>
    ),
  },
  { key: 'status', header: 'Status', render: (s) => <StatusChip status={s.status} /> },
  { key: 'login', header: 'Last sign-in', hideBelow: 'md', render: (s) => time(s.lastLoginAt) },
];

const deviceColumns: Column<Device>[] = [
  { key: 'name', header: 'Device', render: (d) => d.name },
  { key: 'seen', header: 'Last seen', render: (d) => time(d.lastSeenAt) },
  {
    key: 'state',
    header: 'State',
    render: (d) =>
      d.revokedAt ? <StatusChip status="REVOKED" /> : <StatusChip status="ACTIVE" label="Active" />,
  },
];

const syncColumns: Column<SyncError>[] = [
  {
    key: 'error',
    header: 'Error',
    render: (e) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} className="tabular">
          {e.errorCode}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {e.message}
        </Typography>
      </Box>
    ),
  },
  { key: 'cmd', header: 'Command', hideBelow: 'md', render: (e) => e.commandType ?? '—' },
  {
    key: 'retry',
    header: 'Retryable',
    hideBelow: 'sm',
    render: (e) => (e.retryable ? 'Yes' : 'No'),
  },
  { key: 'when', header: 'When', render: (e) => time(e.occurredAt) },
];

const auditColumns: Column<AuditEntry>[] = [
  { key: 'when', header: 'When', render: (a) => time(a.occurredAt) },
  {
    key: 'action',
    header: 'Action',
    render: (a) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }} className="tabular">
          {a.action}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {humanize(a.module)}
        </Typography>
      </Box>
    ),
  },
  {
    key: 'actor',
    header: 'By',
    hideBelow: 'sm',
    render: (a) => a.actorName ?? humanize(a.actorType),
  },
  {
    key: 'outcome',
    header: 'Outcome',
    hideBelow: 'md',
    render: (a) => <StatusChip status={a.outcome} />,
  },
];

function NoGrant({ ended }: { ended?: boolean }) {
  return (
    <Card>
      <EmptyState
        icon={<LockClockOutlined />}
        title={ended ? 'Support access has ended' : 'No support access'}
        body="Support data is only visible while the shop grants access. The shop owner must grant it from Settings → Support access (read-only, up to 72 hours)."
      />
    </Card>
  );
}

function SummaryView({ s }: { s: Summary }) {
  return (
    <Stack spacing={2.5}>
      <Callout severity="info" icon={<SupportAgentRounded />}>
        Read-only access granted by <strong>{s.grant.grantedBy.name}</strong>
        {s.grant.note ? ` (“${s.grant.note}”)` : ''}, ends {relativeTime(s.grant.expiresAt)} (
        {dateTime(s.grant.expiresAt)}). Each view is recorded in the shop’s audit log.
      </Callout>

      <StatGrid min={170}>
        <StatTile label="Products" value={count(s.counts.products)} icon={<Inventory2Outlined />} />
        <StatTile
          label="Contacts"
          value={count(s.counts.contacts)}
          icon={<PeopleAltOutlined />}
          tone="info"
        />
        <StatTile
          label="Sales invoices"
          value={count(s.counts.salesInvoices)}
          icon={<ReceiptLongOutlined />}
          tone="success"
        />
        <StatTile
          label="Purchases"
          value={count(s.counts.purchases)}
          icon={<ShoppingCartOutlined />}
          tone="warning"
        />
        <StatTile
          label="Payments"
          value={count(s.counts.payments)}
          icon={<PaymentsOutlined />}
          tone="neutral"
        />
      </StatGrid>

      <Panel title="Shop settings">
        <FieldGrid>
          <Field label="Name">{s.settings.name}</Field>
          <Field label="Currency">{s.settings.currencyCode}</Field>
          <Field label="Time zone">{s.settings.timezone}</Field>
          <Field label="Point of sale">{s.settings.posEnabled ? 'Enabled' : 'Off'}</Field>
        </FieldGrid>
      </Panel>

      <Section title="Staff" count={s.staff.length}>
        <DataTable
          columns={staffColumns}
          rows={s.staff}
          rowKey={(r) => r.id}
          empty={<Empty text="No staff accounts." />}
        />
      </Section>
      <Section title="Devices" count={s.devices.length}>
        <DataTable
          columns={deviceColumns}
          rows={s.devices}
          rowKey={(r) => r.id}
          empty={<Empty text="No desktop devices registered." />}
        />
      </Section>
      <Section title="Sync errors" count={s.syncErrors.length} hint="Last 20">
        <DataTable
          columns={syncColumns}
          rows={s.syncErrors}
          rowKey={(r) => r.id}
          empty={<Empty text="No sync errors." />}
        />
      </Section>
      <Section title="Recent activity" count={s.recentAudit.length} hint="Last 50 audit entries">
        <DataTable
          columns={auditColumns}
          rows={s.recentAudit}
          rowKey={(r) => r.id}
          empty={<Empty text="No recent activity." />}
        />
      </Section>
    </Stack>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <Typography color="text.secondary" variant="body2" sx={{ p: 2.5, textAlign: 'center' }}>
      {text}
    </Typography>
  );
}

function Section({
  title,
  count: n,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 1.25 }}>
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body2" color="text.secondary" className="tabular">
          {count(n)}
          {hint ? ` · ${hint}` : ''}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

function ActiveSupport({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const summary = useQuery({
    queryKey: tenantKeys.support(tenantId),
    queryFn: () => fetchSupportSummary(tenantId),
  });
  const ended =
    summary.error instanceof ApiError && summary.error.code === 'SUPPORT_ACCESS_REQUIRED';
  // The grant lapsed or was revoked since the tenant loaded: refresh the header/overview too.
  useEffect(() => {
    if (ended) void qc.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) });
  }, [ended, qc, tenantId]);

  if (summary.isPending) return <LoadingRows rows={5} height={64} />;
  if (ended) return <NoGrant ended />;
  if (summary.isError)
    return (
      <Card>
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} dense />
      </Card>
    );
  return <SummaryView s={summary.data} />;
}

export function SupportTab({ tenant }: { tenant: TenantDetail }) {
  if (!tenant.supportAccess)
    return (
      <Card>
        <EmptyState
          icon={<SupportAgentRounded />}
          title="Not available on this server yet"
          body="The API you are connected to does not report support access. It arrives with a newer backend release."
        />
      </Card>
    );
  if (!tenant.supportAccess.active) return <NoGrant />;
  return <ActiveSupport tenantId={tenant.id} />;
}
